import { useEffect, useState } from 'react';
import { ArrowRight, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { formatHa } from '../structureLogic';
import type { ClusterOut, FarmIn, FarmWithAggregate } from '../types';
import { useUpdateFarm } from '../hooks/useStructureQueries';

const NO_CLUSTER = '__none__';

interface MoveClusterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  farm: FarmWithAggregate | null;
  clusters: ClusterOut[];
}

/**
 * Перенос хозяйства в другой кластер (или «Без кластера»). Это просто смена
 * cluster_id через PATCH /farms/{id} — поля и история к полям не трогаются.
 * Обратимо.
 */
export function MoveClusterDialog({
  open,
  onOpenChange,
  farm,
  clusters,
}: MoveClusterDialogProps) {
  const update = useUpdateFarm();
  const [target, setTarget] = useState<string>(NO_CLUSTER);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open && farm) {
      setTarget(farm.cluster_id ?? NO_CLUSTER);
      setError(undefined);
    }
  }, [open, farm]);

  if (!farm) return null;

  const currentClusterName =
    clusters.find((c) => c.id === farm.cluster_id)?.name ?? 'Вне кластера';
  const targetClusterName =
    target === NO_CLUSTER
      ? 'Вне кластера'
      : (clusters.find((c) => c.id === target)?.name ?? 'Вне кластера');

  const unchanged = (farm.cluster_id ?? NO_CLUSTER) === target;

  const submit = async () => {
    if (unchanged) {
      onOpenChange(false);
      return;
    }
    // PATCH требует полный FarmIn — переносим существующие поля, меняем cluster_id.
    const payload: FarmIn = {
      company_id: farm.company_id,
      cluster_id: target === NO_CLUSTER ? null : target,
      name: farm.name,
      short_name: farm.short_name ?? null,
      has_own_legal_entity: farm.has_own_legal_entity,
      legal_entity_name: farm.legal_entity_name ?? null,
      inn: farm.inn ?? null,
      ogrn: farm.ogrn ?? null,
      kpp: farm.kpp ?? null,
      legal_address: farm.legal_address ?? null,
      actual_address: farm.actual_address ?? null,
      director_name: farm.director_name ?? null,
      director_phone: farm.director_phone ?? null,
      director_email: farm.director_email ?? null,
    };
    try {
      await update.mutateAsync({ farmId: farm.id, payload });
      toast.success(`«${farm.name}» перемещено в «${targetClusterName}»`);
      onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError) setError(e.detail);
      else setError('Не удалось переместить хозяйство');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Переместить «{farm.name}» в другой кластер</DialogTitle>
          <DialogDescription>
            Хозяйство и все его {farm.field_count} полей перейдут в выбранный
            кластер целиком. Поля остаются на месте.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Было → станет */}
          <div className="flex items-stretch gap-2">
            <div className="flex-1 rounded-md border border-border bg-background p-3">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Сейчас
              </div>
              <div className="text-sm font-medium">{currentClusterName}</div>
            </div>
            <div className="flex items-center text-muted-foreground">
              <ArrowRight className="h-4 w-4" />
            </div>
            <div className="flex-1 rounded-md border border-accent-brand/50 bg-accent-brand/10 p-3">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Станет
              </div>
              <div className="text-sm font-medium">{targetClusterName}</div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Новый кластер</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CLUSTER}>Без кластера</SelectItem>
                {clusters.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!unchanged && farm.area_ha > 0 && (
            <div className="flex gap-2 rounded-md bg-accent-brand/10 p-3 text-xs text-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-brand" />
              <span>
                Сводки по кластерам пересчитаются: {formatHa(farm.area_ha)} га уйдут
                из «{currentClusterName}» в «{targetClusterName}». Команда, доступы
                и реквизиты хозяйства не меняются.
              </span>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={update.isPending}
          >
            Отмена
          </Button>
          <Button
            onClick={submit}
            disabled={update.isPending || unchanged}
          >
            {update.isPending ? 'Перемещаем…' : 'Переместить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
